import { useEffect, useMemo, useState } from "react";
import { Clock, Search } from "lucide-react";
import { scanIdentity } from "../../lib/scanIdentity";
import {
  ActionButton,
  CustomerInput,
  CustomerPanel,
  CustomerSelect,
  EmptyState,
  StatusBadge,
  TableWrapper,
} from "./CustomerUI";

export const SCANS_PER_PAGE = 10;

const normalized = (value) => String(value ?? "").trim().toLowerCase();

export function scanStatusTone(status) {
  if (status === "completed") return "success";
  if (status === "failed" || status === "cancelled") return "danger";
  if (status === "queued" || status === "running") return "info";
  return "neutral";
}

export function filterScanHistory(scans, { search = "", provider = "all", status = "all" } = {}) {
  const query = normalized(search);
  return scans.filter((scan) => {
    if (provider !== "all" && scan.connector_name !== provider) return false;
    if (status !== "all" && scan.status !== status) return false;
    if (!query) return true;

    return [
      scan.connector_name,
      scan.connection_label,
      scan.status,
      scan.id,
      scan.started_at,
      scan.total_contacts,
      scan.health_score,
    ].some((value) => normalized(value).includes(query));
  });
}

export function paginateScanHistory(scans, page, pageSize = SCANS_PER_PAGE) {
  const pageCount = Math.max(1, Math.ceil(scans.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    pageCount,
    scans: scans.slice(start, start + pageSize),
  };
}

function ScanStatus({ scan }) {
  return (
    <StatusBadge tone={scanStatusTone(scan.status)}>
      <Clock aria-hidden="true" /> {scan.status}
    </StatusBadge>
  );
}

function ScanHealth({ scan, scoreColor }) {
  if (scan.health_score == null) return <span className="text-slate-400">—</span>;
  return (
    <span className={`font-mono font-bold ${scoreColor(scan.health_score)}`}>
      {scan.health_score}/100
    </span>
  );
}

export default function ScanHistory({ scans, onOpen, scoreColor }) {
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const providers = useMemo(
    () => [...new Set(scans.map((scan) => scan.connector_name).filter(Boolean))].sort(),
    [scans],
  );
  const statuses = useMemo(
    () => [...new Set(scans.map((scan) => scan.status).filter(Boolean))].sort(),
    [scans],
  );
  const filtered = useMemo(
    () => filterScanHistory(scans, { search, provider, status }),
    [scans, search, provider, status],
  );
  const pageData = useMemo(() => paginateScanHistory(filtered, page), [filtered, page]);
  const filtersActive = Boolean(search || provider !== "all" || status !== "all");

  useEffect(() => {
    setPage(1);
  }, [search, provider, status]);

  useEffect(() => {
    if (page !== pageData.page) setPage(pageData.page);
  }, [page, pageData.page]);

  const clearFilters = () => {
    setSearch("");
    setProvider("all");
    setStatus("all");
  };

  return (
    <TableWrapper
      title="Recent scans"
      description={
        scans.length > 0
          ? `Search and filter the ${scans.length} most recent scans available in this workspace.`
          : "Run a read-only scan to create your first CRM health report."
      }
    >
      {scans.length === 0 ? (
        <EmptyState
          title="No scans yet"
          description="Run a read-only scan to create your first CRM health report."
        />
      ) : (
        <>
          <div className="grid gap-ui-3 border-b border-slate-200 bg-slate-50/70 p-ui-4 md:grid-cols-[minmax(0,1fr)_12rem_12rem]">
            <label className="block">
              <span className="mb-ui-1 block text-xs font-semibold text-slate-600">Search scan history</span>
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute left-ui-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <CustomerInput
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="CRM, status, or scan ID"
                  className="pl-10"
                  data-testid="scan-history-search"
                />
              </span>
            </label>
            <label className="block">
              <span className="mb-ui-1 block text-xs font-semibold text-slate-600">Provider</span>
              <CustomerSelect
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
                data-testid="scan-history-provider"
              >
                <option value="all">All providers</option>
                {providers.map((name) => <option key={name} value={name}>{name}</option>)}
              </CustomerSelect>
            </label>
            <label className="block">
              <span className="mb-ui-1 block text-xs font-semibold text-slate-600">Status</span>
              <CustomerSelect
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                data-testid="scan-history-status"
              >
                <option value="all">All statuses</option>
                {statuses.map((name) => <option key={name} value={name}>{name}</option>)}
              </CustomerSelect>
            </label>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title="No scans match these filters"
              description="Try a different CRM, status, or search term."
              action={<ActionButton variant="secondary" onClick={clearFilters}>Clear filters</ActionButton>}
            />
          ) : (
            <>
              <div className="hidden md:block">
                <table className="w-full min-w-[48rem] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                      <th className="px-4 py-3 font-medium">CRM</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Scan date</th>
                      <th className="px-4 py-3 font-medium">Contacts</th>
                      <th className="px-4 py-3 font-medium">Health</th>
                      <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.scans.map((scan) => (
                      <tr key={scan.id} data-testid={`scan-row-${scan.id}`} className="border-b border-border last:border-0">
                        <td className="max-w-xs break-words px-4 py-3 font-medium text-slate-900">{scanIdentity(scan).name}<div className="text-xs font-normal text-slate-500">{scanIdentity(scan).provider}</div></td>
                        <td className="px-4 py-3"><ScanStatus scan={scan} /></td>
                        <td className="px-4 py-3 text-slate-600">{scanIdentity(scan).dateLabel}</td>
                        <td className="px-4 py-3 font-mono text-slate-700">{scan.total_contacts}</td>
                        <td className="px-4 py-3"><ScanHealth scan={scan} scoreColor={scoreColor} /></td>
                        <td className="px-4 py-3 text-right">
                          <ActionButton
                            data-testid={`open-scan-${scan.id}`}
                            onClick={() => onOpen(scan.id)}
                            variant="secondary"
                            size="compact"
                          >
                            Open
                          </ActionButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-ui-3 p-ui-3 md:hidden" data-testid="scan-history-mobile">
                {pageData.scans.map((scan) => (
                  <CustomerPanel key={scan.id} data-testid={`scan-card-${scan.id}`} className="p-ui-4">
                    <div className="flex items-start justify-between gap-ui-3">
                      <div className="min-w-0 break-words">
                        <h3 className="font-semibold text-slate-950">{scanIdentity(scan).name}</h3>
                        <p className="mt-ui-1 text-xs text-slate-500">{scanIdentity(scan).provider} · {scanIdentity(scan).dateLabel}</p>
                      </div>
                      <ScanStatus scan={scan} />
                    </div>
                    <dl className="mt-ui-4 grid grid-cols-2 gap-ui-3 border-y border-slate-200 py-ui-3 text-sm">
                      <div>
                        <dt className="text-xs text-slate-500">Contacts</dt>
                        <dd className="mt-ui-1 font-mono font-semibold text-slate-800">{scan.total_contacts}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-slate-500">Health</dt>
                        <dd className="mt-ui-1"><ScanHealth scan={scan} scoreColor={scoreColor} /></dd>
                      </div>
                    </dl>
                    <ActionButton
                      data-testid={`open-scan-mobile-${scan.id}`}
                      onClick={() => onOpen(scan.id)}
                      variant="secondary"
                      className="mt-ui-3 w-full"
                    >
                      Open report
                    </ActionButton>
                  </CustomerPanel>
                ))}
              </div>

              <nav
                className="flex flex-col gap-ui-3 border-t border-slate-200 px-ui-4 py-ui-4 sm:flex-row sm:items-center sm:justify-between"
                aria-label="Scan history pagination"
              >
                <p className="text-center text-xs text-slate-500 sm:text-left" aria-live="polite">
                  Showing {(pageData.page - 1) * SCANS_PER_PAGE + 1}–
                  {Math.min(pageData.page * SCANS_PER_PAGE, filtered.length)} of {filtered.length}
                </p>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-ui-2">
                  <ActionButton
                    variant="secondary"
                    size="compact"
                    disabled={pageData.page === 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Previous
                  </ActionButton>
                  <span className="px-ui-2 text-center text-xs font-semibold text-slate-600">
                    Page {pageData.page} of {pageData.pageCount}
                  </span>
                  <ActionButton
                    variant="secondary"
                    size="compact"
                    disabled={pageData.page === pageData.pageCount}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </ActionButton>
                </div>
              </nav>
            </>
          )}

          {filtersActive && filtered.length > 0 ? (
            <div className="border-t border-slate-200 px-ui-4 py-ui-3 text-right">
              <ActionButton variant="quiet" size="compact" onClick={clearFilters}>Clear filters</ActionButton>
            </div>
          ) : null}
        </>
      )}
    </TableWrapper>
  );
}
